import { useMutation } from "@tanstack/react-query";
import { CheckIcon, EditIcon } from "lucide-react";
import { useEffect, useState } from "react";

import { Alert } from "@/components/base/alert";
import { Button } from "@/components/base/button";
import { Select } from "@/components/base/select";
import type { TshirtSize } from "@/db/schema";
import { useTRPC } from "@/integrations/trpc/react";
import { TSHIRT_SIZE_OPTIONS } from "@/lib/tshirt";

/**
 * Shared t-shirt pickup content: size display with TD edit, and pickup
 * status / action button. Used by the pickup page and the teams-table modal.
 */
export function TshirtSizeDisplay({
  membershipId,
  tshirtSize,
  tshirtPickedUpAt,
  onUpdated,
  onSizeChange,
  onTogglePickup,
  isTogglingPickup,
  isTd,
}: {
  membershipId: number;
  tshirtSize: string | null;
  /** When provided, renders the pickup status and toggle button. */
  tshirtPickedUpAt?: Date | null;
  /** Called after the internal mutation succeeds. Ignored when onSizeChange is provided. */
  onUpdated?: () => void;
  /** When provided, delegates size changes to the caller instead of firing its own mutation. */
  onSizeChange?: (membershipId: number, newSize: string) => void;
  /** Callback to toggle pickup. When omitted, pickup UI is not shown. */
  onTogglePickup?: () => void;
  isTogglingPickup?: boolean;
  /** Whether the current viewer has TD access. Must be provided by the caller. */
  isTd?: boolean;
}) {
  const trpc = useTRPC();

  const [isEditing, setIsEditing] = useState(false);
  const [showSaved, setShowSaved] = useState(false);

  const { mutate: updateSize } = useMutation({
    ...trpc.tournaments.updateTshirtSize.mutationOptions(),
    onSuccess: () => {
      setShowSaved(true);
      setIsEditing(false);
      onUpdated?.();
    },
  });

  useEffect(() => {
    if (!showSaved) return;
    const timer = setTimeout(() => setShowSaved(false), 2000);
    return () => clearTimeout(timer);
  }, [showSaved]);

  const handleSizeChange = (newSize: TshirtSize) => {
    if (onSizeChange) {
      onSizeChange(membershipId, newSize);
      setIsEditing(false);
    } else {
      updateSize({ membershipId, tshirtSize: newSize });
    }
  };

  const isPickedUp = tshirtPickedUpAt != null;
  const showPickup = onTogglePickup !== undefined;

  return (
    <div className="flex flex-col items-center gap-4">
      {tshirtSize && !isEditing && (
        <div className="text-4xl">
          Size: <span className="font-semibold uppercase">{tshirtSize}</span>
          {showSaved && (
            <span className="ml-2 text-base font-normal text-green-600">
              <CheckIcon size={14} className="mb-0.5 inline" /> Saved
            </span>
          )}
        </div>
      )}
      {isTd &&
        (isEditing ? (
          <Select
            label={
              <>
                Size
                {showSaved && (
                  <span className="ml-2 font-normal text-green-600">
                    <CheckIcon size={12} className="mb-0.5 inline" /> Saved
                  </span>
                )}
              </>
            }
            options={TSHIRT_SIZE_OPTIONS}
            selectedKey={tshirtSize ?? undefined}
            onSelectionChange={(key) => {
              if (key) {
                handleSizeChange(key as TshirtSize);
              }
            }}
          />
        ) : (
          <Button variant="text" size="sm" onPress={() => setIsEditing(true)}>
            <EditIcon size={14} />
            Edit Size
          </Button>
        ))}

      {showPickup &&
        (isPickedUp ? (
          isTd ? (
            <Button color="danger" onPress={onTogglePickup}>
              Undo Pickup
            </Button>
          ) : (
            <Alert
              color="success"
              title="Picked Up"
              description="You can close this tab."
            />
          )
        ) : isTd ? (
          <Button
            color="primary"
            onPress={onTogglePickup}
            isDisabled={isTogglingPickup}
          >
            <CheckIcon size={16} />
            Mark as Picked Up
          </Button>
        ) : (
          <Alert
            color="info"
            title="Show to a Tournament Director"
            description="A tournament director can mark your t-shirt as picked up."
          />
        ))}
    </div>
  );
}
